// Copyright (c) 2022 by Move Closer

import { ComputedRef, Ref } from '@vue/composition-api'

import { ImageProps } from '../../atoms/Image'
import { ElementIdentifier } from '@movecloser/page-builder/lib/contracts'
import { AnyObject } from '@movecloser/front-core'

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export interface ContainerBackground {
  color: string
  image: ImageProps
  imagePosition: string
  variantColor: string
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export interface ContainerOffset {
  top: number | string
  bottom: number | string
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export interface ContainerProps {
  /**
   * Background configuration of container.
   */
  background: Partial<ContainerBackground>

  /**
   * Determines color of the font inside container.
   */
  color?: string

  /**
   * Grid gap of container.
   */
  gridGap?: {
    column: string | number
    row: string | number
  }

  /**
   * Element identifier.
   */
  id: ElementIdentifier

  /**
   * Container's inner slot's css class
   */
  innerClass?: string

  /**
   * zIndex.
   */
  zIndex?: number

  /**
   * Inner spacing.
   */
  margin?: ContainerOffset

  /**
   * Inner spacing.
   */
  padding?: ContainerOffset

  /**
   * Width of the container.
   */
  width: string
}

/**
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/object-position
 */
export enum ContainerBackgroundImagePosition {
  Bottom = 'bottom',
  BottomRight = 'bottom right',
  BottomLeft = 'bottom left',
  Center = 'center',
  CenterBottom = 'bottom center',
  CenterLeft = 'center left',
  CenterRight = 'center right',
  CenterTop = 'center top',
  Left = 'left',
  Right = 'right',
  Top = 'top',
  TopLeft = 'top left',
  TopRight = 'top right',
  Fixed = 'fixed'
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export enum ContainerColor {
  Dark = 'dark',
  Grey = 'grey',
  White = 'white',
  Transparent = ''
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export enum ContainerColumnGap {
  Large = 'large',
  None = 0,
  Unset = '',
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export enum ContainerMargin {
  None = 0,
  Spacer1 = 1,
  Spacer1Negative = -1,
  Spacer2 = 2,
  Spacer2Negative = -2,
  Spacer3 = 3,
  Spacer3Negative = -3,
  Spacer4 = 4,
  Spacer4Negative = -4,
  Spacer5 = 5,
  Spacer5Negative = -5,
  Spacer6 = 6,
  Spacer6Negative = -6,
  Spacer7 = 7,
  Spacer7Negative = -7,
  Spacer8 = 8,
  Spacer8Negative = -8,
  Spacer9 = 9,
  Spacer9Negative = -9,
  Spacer10 = 10,
  Spacer10Negative = -10,
  Spacer11 = 11,
  Spacer11Negative = -11,
  Spacer12 = 12,
  Spacer12Negative = -12,
  Spacer13 = 13,
  Spacer13Negative = -13,
  Unset = '',
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export enum ContainerPadding {
  None = 0,
  Spacer1 = 1,
  Spacer2 = 2,
  Spacer3 = 3,
  Spacer4 = 4,
  Spacer5 = 5,
  Spacer6 = 6,
  Spacer7 = 7,
  Spacer8 = 8,
  Spacer9 = 9,
  Spacer10 = 10,
  Unset = '',
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export enum ContainerRowGap {
  None = 0,
  Spacer1 = 1,
  Spacer2 = 2,
  Spacer3 = 3,
  Spacer4 = 4,
  Spacer5 = 5,
  Spacer6 = 6,
  Spacer7 = 7,
  Spacer8 = 8,
  Spacer9 = 9,
  Spacer10 = 10,
  Unset = '',
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export enum ContainerWidth {
  Narrow = 'narrow',
  Normal = 'normal',
  Wide = 'wide',
}

/**
 * @author Piotr Niewczas <piotr.niewczas@movecloser.pl>
 */
export enum ContainerZIndex {
  ZIndexN1 = -1,
  None = 0,
  ZIndex1 = 1,
  ZIndex10 = 10,
  Unset = '',
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export interface UseContainerProvides {
  bgImagePosition: ComputedRef<string>
  fixedBackgroundStyles: ComputedRef<AnyObject>
  hasBackgroundImage: ComputedRef<boolean>
  hasStyleDefined: ComputedRef<boolean>
  hasFixedBackgroundImage: ComputedRef<boolean>
  marginClassName: ComputedRef<string>
  paddingClassName: ComputedRef<string>
  stylesDefinition: ComputedRef<string>
  widthClassName: ComputedRef<string>
  zIndexClassName: ComputedRef<string>
  backgroundVariantClass: ComputedRef<string>
}
